// Empty is not agreement: a field blank in every column is never railed.
// Same rule as Monitoring::DuplicatePeers.field_agreement.
const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === ''

const valuesFor = (sides, key) =>
  sides.map((side) => {
    const hit = side.erp.find((candidate) => candidate.k === key)
    return hit ? hit.v : undefined
  })

export const buildMatchMap = (sides) => {
  const map = {}
  if (!sides.length) return map

  sides[0].erp.forEach((row) => {
    const values = valuesFor(sides, row.k)
    map[row.k] =
      !values.every(isBlank) && values.every((value) => value === values[0])
  })
  return map
}

// Only free-text fields can be "similar"; an ID or date off by a character is just different.
// Display only: the server's score (Monitoring::DuplicateMatchScore) counts exact agreement.
export const SIMILAR_KEYS = ['Vendor name', 'Address']

export const SIMILARITY_THRESHOLD = 0.8

const ABBREVIATIONS = {
  SVC: 'SERVICES', SVCS: 'SERVICES', SERVICE: 'SERVICES',
  CO: 'COMPANY', CORP: 'CORPORATION', INC: 'INCORPORATED',
  LTD: 'LIMITED', MGMT: 'MANAGEMENT', INTL: 'INTERNATIONAL', ASSOC: 'ASSOCIATES',
  ST: 'STREET', AVE: 'AVENUE', AV: 'AVENUE', RD: 'ROAD', DR: 'DRIVE', BLVD: 'BOULEVARD',
  LN: 'LANE', CT: 'COURT', PL: 'PLACE', HWY: 'HIGHWAY', PKWY: 'PARKWAY', STE: 'SUITE',
  N: 'NORTH', S: 'SOUTH', E: 'EAST', W: 'WEST'
}

export const normalizeForSimilarity = (value) =>
  String(value)
    .toUpperCase()
    .replace(/&/g, ' AND ')
    .replace(/[^A-Z0-9 ]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => ABBREVIATIONS[word] || word)
    .join(' ')

const bigrams = (text) => {
  const compact = text.replace(/ /g, '')
  const pairs = []
  for (let i = 0; i < compact.length - 1; i += 1) pairs.push(compact.slice(i, i + 2))
  return pairs
}

// Sørensen–Dice over character pairs: cheap enough to run per row on render.
const dice = (a, b) => {
  const left = bigrams(a)
  const right = bigrams(b)
  if (!left.length || !right.length) return 0

  const counts = new Map()
  left.forEach((pair) => counts.set(pair, (counts.get(pair) || 0) + 1))
  let shared = 0
  right.forEach((pair) => {
    const count = counts.get(pair) || 0
    if (count > 0) {
      shared += 1
      counts.set(pair, count - 1)
    }
  })
  return (2 * shared) / (left.length + right.length)
}

const numbersIn = (text) => (text.match(/\d+/g) || []).join(' ')

// Numbers must agree exactly (123 vs 125 Main St). Containment needs two words, so a
// lone "ACME" doesn't match every ACME-something.
const contained = (left, right) => {
  const [shorter, longer] = [left.split(' '), right.split(' ')].sort((a, b) => a.length - b.length)
  return shorter.length >= 2 && shorter.every((word) => longer.includes(word))
}

export const isSimilar = (a, b) => {
  const left = normalizeForSimilarity(a)
  const right = normalizeForSimilarity(b)
  if (left === right) return true
  if (numbersIn(left) !== numbersIn(right)) return false
  return contained(left, right) || dice(left, right) >= SIMILARITY_THRESHOLD
}

// The compare row each fuzzy rule column lands on (DuplicatePeers::ERP_ROWS).
const ROW_BY_COLUMN = { vendor_name: 'Vendor name', address: 'Address' }

// The analytic's own fuzzy match (`rule.fuzzy_on`) outranks the heuristic.
export const analyticSimilarKeys = (rule) => {
  const key = ROW_BY_COLUMN[rule?.fuzzy_on]
  return key ? [key] : []
}

// Similar needs every column to hold a value; one blank column is a difference.
export const buildStatusMap = (sides, matchMap, analyticKeys = []) => {
  const map = {}
  Object.keys(matchMap).forEach((key) => {
    if (matchMap[key]) {
      map[key] = 'match'
      return
    }
    const values = valuesFor(sides, key)
    const similar =
      SIMILAR_KEYS.includes(key) &&
      values.length > 1 &&
      !values.some(isBlank) &&
      (analyticKeys.includes(key) || values.every((value) => isSimilar(value, values[0])))
    map[key] = similar ? 'similar' : 'differs'
  })
  return map
}
